/**
 * Matches `[label](https://…)` or a bare http(s) address. Only http and https
 * are accepted, so a description can't carry a javascript: link.
 */
const LINK_PATTERN = /\[([^\]\n]{1,200})\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+)/gi

/** Punctuation that usually ends the sentence rather than the address. */
const TRAILING = /[.,;:!?'")\]]+$/

export interface TextPart {
  text: string
  href?: string
}

/** Splits text into plain runs and links. */
export function splitLinks(text: string): TextPart[] {
  const parts: TextPart[] = []
  let last = 0

  for (const match of text.matchAll(LINK_PATTERN)) {
    const start = match.index ?? 0
    const [whole, label, labelledUrl, bareUrl] = match
    let end = start + whole.length
    let part: TextPart

    if (labelledUrl !== undefined) {
      part = { text: label, href: labelledUrl }
    } else {
      // "see https://x.gg." -- leave the full stop out of the link, but keep a
      // closing bracket when the address opened one, as Wikipedia links do.
      let url = bareUrl
      const trailing = TRAILING.exec(url)?.[0] ?? ''
      let keep = url.length - trailing.length
      if (trailing.startsWith(')') && url.slice(0, keep).includes('(')) keep += 1
      url = url.slice(0, keep)
      end = start + url.length
      part = { text: url, href: url }
    }

    if (!isSafeUrl(part.href!)) continue
    if (start > last) parts.push({ text: text.slice(last, start) })
    parts.push(part)
    last = end
  }

  if (last < text.length) parts.push({ text: text.slice(last) })
  return parts
}

function isSafeUrl(href: string): boolean {
  try {
    const url = new URL(href)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}
